import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";

// A plan cell's dropdown on Create Schedule (AdminLessonPlan.jsx): click an item to give it to this
// child, or its "Select all" to give it to every child in the column ("Clear all" on "—"); cells
// picked by hand for other children keep their pick.
// The list is drawn on <body> (fixed, next to the cell) because the plan table scrolls sideways and
// would clip it; it closes when the page scrolls, like the calendar's ⋮ menu.
const SEARCH_FROM = 8;
const PANEL_WIDTH = 290;
// The list is measured before it is placed; opacity (not visibility) so its search box can take focus.
const MEASURING = { opacity: 0, pointerEvents: "none", width: PANEL_WIDTH };

export default function LessonPlanPicker({ value, groups, onChange, onSelectAll, label, icon, allowEmpty = true, emptyLabel = "—" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const close = useCallback(() => { setOpen(false); setQuery(""); }, []);

  // A saved value that is no longer offered stays selectable.
  const listed = groups.some((g) => g.options.includes(value));
  const allGroups = value && !listed ? [{ label: "Current", options: [value] }, ...groups] : groups;
  const total = allGroups.reduce((n, g) => n + g.options.length, 0);
  const q = query.trim().toLowerCase();
  const visible = allGroups
    .map((g) => ({ ...g, options: g.options.filter((o) => o.toLowerCase().includes(q)) }))
    .filter((g) => g.options.length);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const cell = buttonRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!cell || !panel) return;
    const below = cell.bottom + 3;
    const top = below + panel.height <= window.innerHeight - 8 ? below : Math.max(8, cell.top - 3 - panel.height);
    const left = Math.max(8, Math.min(cell.left, window.innerWidth - panel.width - 8));
    setPos({ top, left, width: Math.max(cell.width, PANEL_WIDTH) });
  }, [open, query]);

  useEffect(() => {
    if (!open) return undefined;
    const inside = (target) => buttonRef.current?.contains(target) || panelRef.current?.contains(target);
    const onOutside = (e) => { if (!inside(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") { close(); buttonRef.current?.focus(); } };
    const onScroll = (e) => { if (!panelRef.current?.contains(e.target)) close(); };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  // Short lists have no search box: start keyboard users on the chosen item.
  useEffect(() => {
    if (!open || !pos || panelRef.current?.contains(document.activeElement)) return;
    panelRef.current?.querySelector(".is-selected .lp-pick__option, .lp-pick__option")?.focus({ preventScroll: true });
  }, [open, pos]);

  // Arrow keys move between the items (and their Select all buttons).
  const onPanelKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = [...(panelRef.current?.querySelectorAll("[data-pick]") || [])];
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement);
    const next = e.key === "ArrowDown" ? Math.min(items.length - 1, at + 1) : Math.max(0, at - 1);
    items[at < 0 ? 0 : next].focus();
  };

  const pick = (option) => { onChange(option); close(); buttonRef.current?.focus(); };
  const pickAll = (option) => { onSelectAll(option); close(); buttonRef.current?.focus(); };
  const shown = icon?.(value);

  const row = (option, text, allLabel, allAria) => {
    const selected = option === (value || "");
    const meta = option ? icon?.(option) : null;
    return (
      <li key={option || "empty"} className={`lp-pick__row${selected ? " is-selected" : ""}`}>
        <button type="button" className="lp-pick__option" data-pick onClick={() => pick(option)}>
          {meta && <meta.Icon size={15} color={meta.color} aria-hidden="true" />}
          <span>{text}</span>
          {selected && <Check size={14} className="lp-pick__tick" aria-hidden="true" />}
        </button>
        {onSelectAll && (
          <button type="button" className="lp-pick__all" data-pick aria-label={allAria} title={allAria} onClick={() => pickAll(option)}>
            {allLabel}
          </button>
        )}
      </li>
    );
  };

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className={`lp-pick__trigger${open ? " is-open" : ""}`}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={value || undefined}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => { if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); } }}
      >
        {shown && <shown.Icon size={16} color={shown.color} aria-hidden="true" />}
        <span>{value || emptyLabel}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && createPortal(
        <div
          className="lp-pick"
          role="dialog"
          aria-label={`${label} options`}
          ref={panelRef}
          onKeyDown={onPanelKey}
          style={pos ? { top: pos.top, left: pos.left, width: pos.width } : MEASURING}
        >
          {total >= SEARCH_FROM && (
            <label className="lp-pick__search">
              <Search size={13} aria-hidden="true" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" aria-label={`Search ${label}`} />
            </label>
          )}
          <ul>
            {allowEmpty && !q && row("", emptyLabel, "Clear all", `Clear ${label.replace(/ for .*$/, "")} for all children`)}
            {visible.map((g) => (
              <React.Fragment key={g.label}>
                {g.label && <li className="lp-pick__group">{g.label}</li>}
                {g.options.map((o) => row(o, o, "Select all", `Select ${o} for all children`))}
              </React.Fragment>
            ))}
            {!visible.length && (q || !allowEmpty) && <li className="lp-pick__empty">{q ? "No matches" : "Nothing to choose yet"}</li>}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
